import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Match, MatchType } from '../../types/sports';
import { useAuth } from '../../contexts/AuthContext';
import { matchdayPlanningApi } from '../../services/matchdayPlanningApi';
import { matchdayPlanningEntitiesApi } from '../../services/matchdayPlanningEntitiesApi';
import { MatchdayPlanning, VehicleAssignment, VehicleType, StaffAssignment, StaffRole, TravelerInfo } from '../../types/matchdayPlanning';
import { exportToCalendar, matchToCalendarEvent } from '../../utils/calendarExport';

interface UpcomingMatchCardProps {
  match: Match;
  onPlanMatch?: (match: Match) => void;
  onDeleteMatch?: (match: Match) => void;
  backgroundColor?: string;
}

// Vehicle type labels
const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  bus: 'Bus',
  minibus: 'Minibus',
  car: 'PKW',
  plane: 'Flug',
  train: 'Bahn',
  taxi: 'Taxi',
  rentalcar: 'Mietwagen',
  ship: 'Schiff',
  bicycle: 'Rad',
  onfoot: 'Fuß'
};

const roleMap: Record<string, StaffRole> = {
  'Physio': StaffRole.Physio,
  'Arzt': StaffRole.Arzt,
  'Athletik': StaffRole.Athletik,
  '0': StaffRole.Physio,
  '1': StaffRole.Arzt,
  '2': StaffRole.Athletik
};

const UpcomingMatchCard: React.FC<UpcomingMatchCardProps> = ({ match, onPlanMatch, onDeleteMatch, backgroundColor }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [planning, setPlanning] = useState<MatchdayPlanning | null>(null);
  const [isLoadingPlanning, setIsLoadingPlanning] = useState(true);
  const [staffMembers, setStaffMembers] = useState<TravelerInfo[]>([]);
  const [vehicleAssignments, setVehicleAssignments] = useState<VehicleAssignment[]>([]);
  const [staffAssignments, setStaffAssignments] = useState<StaffAssignment[]>([]);
  const [savingCell, setSavingCell] = useState<string | null>(null);

  // Load staff members on mount
  useEffect(() => {
    matchdayPlanningEntitiesApi.getStaffMembers()
      .then(setStaffMembers)
      .catch(err => console.error('Error loading staff members:', err));
  }, []);

  // Load planning data for this match
  useEffect(() => {
    const loadPlanning = async () => {
      try {
        const existingPlanning = await matchdayPlanningApi.getPlanningByEvent(undefined, match.id);
        setPlanning(existingPlanning);
        setVehicleAssignments(existingPlanning?.vehicleAssignments || []);
        setStaffAssignments(existingPlanning?.staffAssignments || []);
      } catch (error) {
        console.error('Error loading planning for match:', error);
      } finally {
        setIsLoadingPlanning(false);
      }
    };
    loadPlanning();
  }, [match.id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string, kickoffTime?: string) => {
    if (kickoffTime) return kickoffTime.substring(0, 5);
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const getMatchType = (): 'home' | 'away' | null => {
    if (!user?.preferredClubId) return null;
    if (match.homeClubId === user.preferredClubId) return 'home';
    if (match.awayClubId === user.preferredClubId) return 'away';
    return null;
  };

  const getStaffByRole = (role: StaffRole): StaffAssignment | undefined => {
    return staffAssignments.find(sa => {
      const saRole = typeof sa.role === 'string' ? roleMap[sa.role] : sa.role;
      return saRole === role;
    });
  };

  const handleStaffChange = async (role: StaffRole, travelerId: number | null) => {
    const cellKey = `${match.id}-${role}`;
    setSavingCell(cellKey);
    try {
      let planningId = planning?.id;

      // Create planning if none exists
      if (!planningId) {
        const matchDateStr = match.date;
        const matchDate = new Date(matchDateStr);
        const newPlanning = await matchdayPlanningApi.createPlanning({
          matchId: match.id,
          name: `${match.homeClub?.name || 'Heim'} vs ${match.awayClub?.name || 'Gast'}`,
          date: matchDateStr,
          kickoffTime: matchDate.toTimeString().slice(0, 5),
          arrivalLeadMinutes: 60,
        });
        planningId = newPlanning.id;
        setPlanning(newPlanning);
      }

      const existingAssignment = getStaffByRole(role);

      if (travelerId === null || travelerId === 0) {
        if (existingAssignment) {
          await matchdayPlanningApi.deleteStaffAssignment(existingAssignment.id);
          setStaffAssignments(prev => prev.filter(sa => sa.id !== existingAssignment.id));
        }
      } else if (existingAssignment) {
        const updated = await matchdayPlanningApi.updateStaffAssignment(existingAssignment.id, { travelerId });
        setStaffAssignments(prev => prev.map(sa => sa.id === existingAssignment.id ? updated : sa));
      } else {
        const created = await matchdayPlanningApi.createStaffAssignment({
          planningId: planningId!,
          travelerId,
          role
        });
        setStaffAssignments(prev => [...prev, created]);
      }
    } catch (error) {
      console.error('Error updating staff assignment:', error);
    } finally {
      setSavingCell(null);
    }
  };

  const matchType = getMatchType();

  // Resolve display names with free-text fallback for own matches (Testspiel/Training)
  const homeName = match.homeClub?.name || match.homeTeamName || '—';
  const awayName = match.awayClub?.name || match.awayTeamName || '—';
  const ownMatchLabel =
    match.matchType === MatchType.Training ? 'Training' :
    match.matchType === MatchType.Friendly ? 'Testspiel' : null;

  return (
    <div style={{
      ...cardStyle,
      ...(backgroundColor ? {
        backgroundColor,
        borderLeft: `6px solid ${backgroundColor}`,
        backgroundImage: `linear-gradient(to right, ${backgroundColor}15, white)`
      } : {})
    }}>
      {/* Match Header */}
      <div style={headerStyle}>
        <div style={dateTimeStyle}>
          <div style={spieltagLabelStyle}>{match.matchday.name}</div>
          <div style={dateStyle}>{formatDate(match.date || match.scheduledDate)}</div>
          <div style={timeStyle}>
            {isLoadingPlanning ? '...' : formatTime(match.date || match.scheduledDate, planning?.kickoffTime)}
          </div>
        </div>
        <div style={statusStyle}>
          {ownMatchLabel && (
            <span style={{ ...matchTypeBadgeStyle, backgroundColor: '#6c757d' }}>
              {ownMatchLabel}
            </span>
          )}
          {matchType && (
            <span style={{
              ...matchTypeBadgeStyle,
              backgroundColor: matchType === 'home' ? (user?.homeMatchColor || '#4CAF50') : (user?.awayMatchColor || '#FF9800')
            }}>
              {matchType === 'home' ? 'Heim' : 'Auswärts'}
            </span>
          )}
        </div>
      </div>

      {/* Teams */}
      <div style={teamsStyle}>
        <div style={teamStyle}>
          <div style={teamNameStyle}>{homeName}</div>
          {match.homeClub?.city && <div style={teamCityStyle}>{match.homeClub.city}</div>}
        </div>
        <div style={vsStyle}>
          <span style={vsTextStyle}>vs</span>
          {match.homeScore !== undefined && match.awayScore !== undefined && (
            <div style={scoreStyle}>{match.homeScore} : {match.awayScore}</div>
          )}
        </div>
        <div style={teamStyle}>
          <div style={teamNameStyle}>{awayName}</div>
          {match.awayClub?.city && <div style={teamCityStyle}>{match.awayClub.city}</div>}
        </div>
      </div>

      {/* Location (own matches without a stadium-bearing club) */}
      {match.location && (
        <div style={locationRowStyle}>
          <span style={detailLabelStyle}>Ort:</span>
          <span style={{ fontSize: '12px', color: '#2c3e50', fontWeight: 600 }}>{match.location}</span>
        </div>
      )}

      {/* Details: Vehicles, Drivers, Staff (like Plantafel) */}
      <div style={detailsStyle}>
        {/* Fahrzeuge */}
        <div style={detailRowStyle}>
          <span style={detailLabelStyle}>Fahrzeuge:</span>
          <div style={vehicleCellStyle}>
            {vehicleAssignments.length > 0 ? vehicleAssignments.map((va, idx) => (
              <div key={va.id || idx} style={vehicleChipStyle}>
                <span style={{ fontSize: 11, fontWeight: 600 }}>{VEHICLE_TYPE_LABELS[va.vehicleType]}</span>
                <span style={driverNameStyle}>{va.traveler?.fullName || va.partnerDriver?.fullName || '-'}</span>
              </div>
            )) : <span style={noDataStyle}>-</span>}
          </div>
        </div>

        {/* Staff: Physio */}
        <div style={detailRowStyle}>
          <span style={detailLabelStyle}>Physio:</span>
          <select
            style={staffSelectStyle}
            value={getStaffByRole(StaffRole.Physio)?.travelerId || ''}
            onChange={(e) => handleStaffChange(StaffRole.Physio, e.target.value ? parseInt(e.target.value) : null)}
            disabled={savingCell === `${match.id}-${StaffRole.Physio}`}
          >
            <option value="">-</option>
            {staffMembers.map(s => <option key={s.id} value={s.id}>{s.fullName}</option>)}
          </select>
        </div>

        {/* Staff: Arzt */}
        <div style={detailRowStyle}>
          <span style={detailLabelStyle}>Arzt:</span>
          <select
            style={staffSelectStyle}
            value={getStaffByRole(StaffRole.Arzt)?.travelerId || ''}
            onChange={(e) => handleStaffChange(StaffRole.Arzt, e.target.value ? parseInt(e.target.value) : null)}
            disabled={savingCell === `${match.id}-${StaffRole.Arzt}`}
          >
            <option value="">-</option>
            {staffMembers.map(s => <option key={s.id} value={s.id}>{s.fullName}</option>)}
          </select>
        </div>

        {/* Staff: Athletik */}
        <div style={detailRowStyle}>
          <span style={detailLabelStyle}>Athletik:</span>
          <select
            style={staffSelectStyle}
            value={getStaffByRole(StaffRole.Athletik)?.travelerId || ''}
            onChange={(e) => handleStaffChange(StaffRole.Athletik, e.target.value ? parseInt(e.target.value) : null)}
            disabled={savingCell === `${match.id}-${StaffRole.Athletik}`}
          >
            <option value="">-</option>
            {staffMembers.map(s => <option key={s.id} value={s.id}>{s.fullName}</option>)}
          </select>
        </div>
      </div>

      {/* Action Buttons: Planung, Route, Tracking, Kalender */}
      <div style={actionsStyle}>
        <button
          style={planButtonStyle}
          onClick={() => {
            if (onPlanMatch) onPlanMatch(match);
            else if (planning?.id) navigate(`/matchday-planning/${planning.id}`);
            else navigate(`/matchday-planning?matchId=${match.id}`);
          }}
        >
          Planung
        </button>
        {planning?.id && (
          <>
            <button
              style={routeButtonStyle}
              onClick={() => navigate(`/matchday-planning/${planning.id}#route`)}
            >
              Route
            </button>
            <button
              style={trackingButtonStyle}
              onClick={() => navigate(`/tracking?planningId=${planning.id}`)}
            >
              Tracking
            </button>
          </>
        )}
        <button
          style={calendarButtonStyle}
          onClick={() => exportToCalendar(matchToCalendarEvent(match))}
          title="Zum Kalender hinzufügen"
        >
          📅
        </button>
        {ownMatchLabel && onDeleteMatch && (
          <button
            style={deleteButtonStyle}
            onClick={() => onDeleteMatch(match)}
            title="Spiel löschen"
          >
            🗑
          </button>
        )}
      </div>
    </div>
  );
};

// Styles
const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e9ecef',
  transition: 'all 0.3s ease',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '12px'
};

const dateTimeStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px'
};

const spieltagLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: '700',
  color: '#6c757d',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const dateStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#2c3e50'
};

const timeStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: 'bold',
  color: '#3498db'
};

const statusStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap'
};

const matchTypeBadgeStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: '700',
  color: 'white',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px'
};

const teamsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: '12px',
  alignItems: 'center',
  marginBottom: '12px'
};

const teamStyle: React.CSSProperties = { textAlign: 'center' };

const teamNameStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: '600',
  color: '#2c3e50',
  marginBottom: '2px'
};

const teamCityStyle: React.CSSProperties = { fontSize: '11px', color: '#7f8c8d' };

const vsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px'
};

const vsTextStyle: React.CSSProperties = { fontSize: '14px', fontWeight: '600', color: '#95a5a6' };

const scoreStyle: React.CSSProperties = { fontSize: '18px', fontWeight: 'bold', color: '#2c3e50' };

const locationRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
  padding: '8px 10px',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px'
};

const detailsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginBottom: '12px',
  padding: '10px',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px'
};

const detailRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px'
};

const detailLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#7f8c8d',
  fontWeight: '500',
  whiteSpace: 'nowrap'
};

const vehicleCellStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' };
const vehicleChipStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(0,0,0,0.08)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' };
const driverNameStyle: React.CSSProperties = { color: '#333', fontWeight: 600 };
const noDataStyle: React.CSSProperties = { color: '#999', fontStyle: 'italic', fontSize: '12px' };

const staffSelectStyle: React.CSSProperties = {
  padding: '3px 6px',
  fontSize: '12px',
  fontWeight: 500,
  border: 'none',
  borderRadius: '4px',
  backgroundColor: 'rgba(0,0,0,0.08)',
  cursor: 'pointer',
  minWidth: '90px',
  maxWidth: '140px',
  appearance: 'none' as const,
  WebkitAppearance: 'none' as const,
  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'8\' viewBox=\'0 0 24 24\'%3E%3Cpath fill=\'%23666\' d=\'M7 10l5 5 5-5z\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 4px center',
  paddingRight: '16px',
  color: '#333',
  outline: 'none'
};

const actionsStyle: React.CSSProperties = { display: 'flex', gap: '6px' };
const planButtonStyle: React.CSSProperties = { flex: 1, backgroundColor: '#007bff', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };
const routeButtonStyle: React.CSSProperties = { flex: 1, backgroundColor: '#28a745', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };
const trackingButtonStyle: React.CSSProperties = { flex: 1, backgroundColor: '#6f42c1', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };
const calendarButtonStyle: React.CSSProperties = { backgroundColor: '#fd7e14', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, minWidth: '36px' };
const deleteButtonStyle: React.CSSProperties = { backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, minWidth: '36px' };

export default UpcomingMatchCard;
